import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { ICellRendererParams } from 'ag-grid-community';
import { Role } from '../role-permission-management/role-permission-management.models';
import { RbacService } from '../../services/rbac/rbac.service';
import { RbacPermissions } from '../../services/rbac/rbac-permissions';

@Component({
  selector: 'app-roles-action-cell-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, CommonModule],
  template: `
    <span class="action-buttons" *ngIf="canEdit || canDelete">
      <button
        type="button"
        class="action-btn action-btn--edit"
        *ngIf="canEdit"
        (click)="onEdit($event)"
        title="Edit"
      >
        <mat-icon>edit</mat-icon>
      </button>
      <button
        type="button"
        class="action-btn action-btn--delete"
        *ngIf="canDelete"
        (click)="onDelete($event)"
        title="Delete"
      >
        <mat-icon>delete</mat-icon>
      </button>
    </span>
  `,
  styles: [
    `
      .action-buttons {
        display: flex;
        gap: 6px;
        align-items: center;
        height: 100%;
      }

      .action-btn {
        border: none;
        padding: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 6px;
        transition: background 0.15s ease, box-shadow 0.15s ease;
        outline: none;
      }

      .action-btn mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
        line-height: 18px;
      }

      .action-btn--edit {
        cursor: pointer;
        background: #e8f0f2;
        border: 1px solid #c5d8dc;
      }
      .action-btn--edit mat-icon {
        color: #3a6570;
      }
      .action-btn--edit:hover {
        background: #d0e6ea;
        box-shadow: 0 1px 4px rgba(58, 101, 112, 0.25);
      }

      .action-btn--delete {
        cursor: pointer;
        background: #fdecea;
        border: 1px solid #f5b8b2;
      }
      .action-btn--delete mat-icon {
        color: #d32f2f;
        transition: color 0.15s ease;
      }
      .action-btn--delete:hover {
        background: #f9d0cc;
        box-shadow: 0 1px 4px rgba(211, 47, 47, 0.3);
      }
      .action-btn--delete:hover mat-icon {
        color: #b71c1c;
      }
    `,
  ],
})
export class RolesActionCellRendererComponent implements ICellRendererAngularComp {
  private readonly rbacService = inject(RbacService);

  params!: ICellRendererParams<Role, unknown> & {
    onEdit?: (row: Role) => void;
    onDelete?: (row: Role) => void;
    editPermission?: string;
    deletePermission?: string;
  };

  canEdit = true;
  canDelete = true;

  agInit(params: ICellRendererParams<Role>): void {
    this.params = params as typeof this.params;
    this.refreshPermissions();
  }

  refresh(params: ICellRendererParams<Role>): boolean {
    this.params = params as typeof this.params;
    this.refreshPermissions();
    return true;
  }

  private refreshPermissions(): void {
    const editPermission =
      this.params?.editPermission ?? RbacPermissions.UserMgmt_RolePermission;
    const deletePermission =
      this.params?.deletePermission ?? RbacPermissions.UserMgmt_RolePermission;

    this.canEdit = this.rbacService.hasPermission(editPermission);
    this.canDelete = this.rbacService.hasPermission(deletePermission);
  }

  onEdit(event: Event): void {
    event.stopPropagation();
    const data = this.params?.data;
    if (data) {
      this.params?.onEdit?.(data);
    }
  }

  onDelete(event: Event): void {
    event.stopPropagation();
    const data = this.params?.data;
    if (data) {
      this.params?.onDelete?.(data);
    }
  }
}
